import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import {
  joinApiBaseAndPath,
  normalizeBrowserApiUrl,
} from "../utils/alertDetectionMedia";

/**
 * GET /cameras/{camera_id} — JSON detail for zone configuration (stream, preview, metadata).
 */

export interface CameraDetail {
  id: string;
  name: string;
  stream_url: string;
  /** Shown in zone config header; never use `stream_url` for that label. */
  camera_ip?: string;
  is_active: boolean;
  /** When true, use `stream_url` as returned by the API (auth-only / server-managed URL). */
  allow_auth_only?: boolean;
  streaming_username?: string;
  streaming_password?: string;
  zones_count?: number;
  preview_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

function parseCameraDetailPayload(body: unknown): CameraDetail | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  const data = (obj.data ?? obj) as Record<string, unknown>;
  if (!data || typeof data !== "object") return null;
  const id = data.id != null ? String(data.id).trim() : "";
  if (!id) return null;
  const cameraIpRaw = data.camera_ip ?? data.cameraIp;
  const allowAuthRaw = data.allow_auth_only ?? data.allowAuthOnly;
  const streamUserRaw = data.streaming_username ?? data.streamingUsername;
  const streamPassRaw = data.streaming_password ?? data.streamingPassword;
  return {
    id,
    name: data.name != null ? String(data.name) : "",
    stream_url: data.stream_url != null ? String(data.stream_url) : "",
    camera_ip:
      cameraIpRaw != null && String(cameraIpRaw).trim() !== ""
        ? String(cameraIpRaw).trim()
        : undefined,
    is_active: Boolean(data.is_active),
    allow_auth_only:
      allowAuthRaw === true ||
      allowAuthRaw === 1 ||
      (typeof allowAuthRaw === "string" &&
        allowAuthRaw.trim().toLowerCase() === "true"),
    streaming_username:
      streamUserRaw != null && String(streamUserRaw).trim() !== ""
        ? String(streamUserRaw).trim()
        : undefined,
    streaming_password:
      streamPassRaw != null ? String(streamPassRaw) : undefined,
    zones_count:
      typeof data.zones_count === "number"
        ? data.zones_count
        : data.zones_count != null
          ? Number(data.zones_count) || undefined
          : undefined,
    preview_url:
      data.preview_url == null || data.preview_url === ""
        ? null
        : String(data.preview_url),
    created_at:
      data.created_at != null ? String(data.created_at) : undefined,
    updated_at:
      data.updated_at != null ? String(data.updated_at) : undefined,
  };
}

/** Turn API `preview_url` (often `/ai/cameras/{id}/preview`) into a full browser URL. */
export function resolveCameraMediaUrl(
  apiBase: string,
  pathOrAbsolute: string | null | undefined,
): string {
  if (pathOrAbsolute == null || String(pathOrAbsolute).trim() === "") return "";
  const s = String(pathOrAbsolute).trim();
  if (/^https?:\/\//i.test(s)) return normalizeBrowserApiUrl(s);
  const base = apiBase.replace(/\/$/, "");
  if (!base) return s.startsWith("/") ? s : `/${s}`;
  return normalizeBrowserApiUrl(joinApiBaseAndPath(base, s));
}

export async function fetchCameraById(
  apiBase: string,
  cameraId: string,
  signal?: AbortSignal,
): Promise<CameraDetail> {
  const base = apiBase.replace(/\/$/, "");
  if (!base) {
    throw new Error("API base URL is not configured");
  }
  const url = `${base}/cameras/${encodeURIComponent(cameraId)}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json", ...userTimezoneHeaders() },
    signal,
  });
  const text = await res.text();
  let body: unknown = {};
  if (text.trim()) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      throw new Error("Invalid JSON from camera detail");
    }
  }
  if (!res.ok) {
    const detail = (body as { detail?: unknown })?.detail;
    let msg = res.statusText || `HTTP ${res.status}`;
    if (typeof detail === "string") msg = detail;
    else if (Array.isArray(detail)) {
      const parts = detail
        .map((d: { msg?: string }) => d?.msg)
        .filter(Boolean) as string[];
      if (parts.length) msg = parts.join("; ");
    }
    throw new Error(msg);
  }
  const parsed = parseCameraDetailPayload(body);
  if (!parsed) {
    throw new Error("Invalid camera response shape");
  }
  return parsed;
}

/**
 * GET /cameras/{id} raw `data` object for mapping into the add/edit camera form
 * (fields like `type`, `api_base_url`, `port`, `user`, `password`).
 */
export async function fetchCameraRawData(
  apiBase: string,
  cameraId: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown>> {
  const base = apiBase.replace(/\/$/, "");
  if (!base) {
    throw new Error("API base URL is not configured");
  }
  const url = `${base}/cameras/${encodeURIComponent(cameraId)}`;
  /** Content negotiation: prefer JSON, not preview image (image/* when Accept is broad). */
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...userTimezoneHeaders(),
    },
    signal,
  });
  const contentType = (res.headers.get("content-type") ?? "").toLowerCase();
  if (contentType.includes("image/")) {
    throw new Error(
      "Server returned a preview image instead of camera JSON. Use Accept: application/json on GET /cameras/{id}.",
    );
  }
  const text = await res.text();
  let body: unknown = {};
  if (text.trim()) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      throw new Error("Invalid JSON from camera detail");
    }
  }
  if (!res.ok) {
    const detail = (body as { detail?: unknown })?.detail;
    let msg = res.statusText || `HTTP ${res.status}`;
    if (typeof detail === "string") msg = detail;
    else if (Array.isArray(detail)) {
      const parts = detail
        .map((d: { msg?: string }) => d?.msg)
        .filter(Boolean) as string[];
      if (parts.length) msg = parts.join("; ");
    }
    throw new Error(msg);
  }
  const obj = body as Record<string, unknown>;
  const data = (obj.data ?? obj) as Record<string, unknown>;
  if (!data || typeof data !== "object") {
    throw new Error("Invalid camera response shape");
  }
  return data;
}

/**
 * `stream_url` for POST /cameras (e.g. AI Models Save sync).
 * - When `allow_auth_only` is true: use the camera detail `stream_url` from the API.
 * - Otherwise: `rtsp://{streaming_username}:{streaming_password}@{camera_ip}:554/`
 *   (credentials percent-encoded; empty IP yields "").
 */
export function buildStreamUrlForCameraPostPayload(
  detail: CameraDetail | null | undefined,
  fallbackIp: string,
): string {
  const ip =
    (detail?.camera_ip?.trim() ||
      String(fallbackIp ?? "")
        .replace(/^\/+/, "")
        .trim()) ||
    "";
  if (detail?.allow_auth_only === true) {
    return detail.stream_url?.trim() ?? "";
  }
  if (!ip) return "";
  const user = (detail?.streaming_username ?? "").trim();
  const pass = detail?.streaming_password ?? "";
  return `rtsp://${encodeURIComponent(user)}:${encodeURIComponent(pass)}@${ip}:554/`;
}
